/**
 * ATTEST-UX-2 D3 — a step's Continue, gated on that step's own required fields.
 *
 * Until now only Step 1 gated. A CPA could walk Steps 2→5 with an empty subject
 * entity, no consent box and a statement with no source, and first hear about it
 * on Step 5 — three screens from where the answer belongs. The asterisks were
 * already there; nothing acted on them.
 *
 * What gates here is deliberately narrow: **presence of a required answer, and
 * nothing else.** Those are the only questions a form can settle by itself. The
 * checks that decide whether the letter may issue — the allow list, assurance
 * wording, evidence authenticity, the independence engine — stay on the server
 * and stay at Build, which is the same doctrine `PreflightChecklist.tsx` is
 * written to. A client that blocks on a rule it only half-knows is worse than
 * one that waits: it teaches the CPA that the wizard's "no" is negotiable.
 *
 * Backward navigation is untouched, and so is the step nav: Step 5 has to stay
 * reachable while fields are missing, because Step 5 is where the full list of
 * them is shown with a jump to each one.
 */
import type { RequiredFieldEntry } from '@/components/attest/requiredFields'
import { Button } from '@/components/ui/button'

export interface StepContinueProps {
  /** This step's still-missing required fields. Empty ⇒ never blocked. */
  missing: RequiredFieldEntry[]
  onContinue: () => void
  /** Land on one of them. Same focus path the preflight's jump uses. */
  onCorrect?: (fieldId: string) => void
  /** `data-testid` of the button; the hint derives its own from it. */
  testId: string
  label?: string
  /** Kept so gating a step does not restyle its existing button. */
  variant?: 'default' | 'outline'
}

export function StepContinue({
  missing, onContinue, onCorrect, testId, label = 'Continue',
  variant = 'default',
}: StepContinueProps) {
  const blocked = missing.length > 0

  return (
    <div className="space-y-2" data-blocked={blocked ? 'true' : 'false'}>
      {blocked && (
        <div
          data-testid={`${testId}-blocked`}
          className="rounded-lg border border-amber-300 bg-amber-50 p-3"
        >
          <p className="text-sm font-medium text-amber-900">
            {missing.length === 1
              ? 'One answer on this step is still empty:'
              : `${missing.length} answers on this step are still empty:`}
          </p>
          <ul className="mt-2 space-y-1">
            {missing.map(entry => (
              <li
                key={entry.id}
                data-testid={`gate-${entry.id}`}
                className="flex flex-col items-start gap-1 text-sm text-amber-900 sm:flex-row sm:flex-wrap sm:items-baseline sm:justify-between sm:gap-2"
              >
                <span className="min-w-0 flex-1">{entry.missingText}</span>
                {onCorrect && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`gate-goto-${entry.id}`}
                    onClick={() => onCorrect(entry.id)}
                  >
                    Correct here
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Button
        variant={variant}
        onClick={onContinue}
        disabled={blocked}
        data-testid={testId}
      >
        {label}
      </Button>
    </div>
  )
}
